import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
export interface NpcStrideClips {
  walk: {stride_length: number; duration: number};
  run: {stride_length: number; duration: number};
}

/** Owns authored skeletal clips; kinematic movement remains with NPCBehavior. */
export class GroundedNpcGait {
  private readonly groups: Record<"idle" | "walk" | "run", AnimationGroup>;
  private suspended = false;

  static create(groups: readonly AnimationGroup[], scale: number, clips: NpcStrideClips): GroundedNpcGait | null {
    const byName = new Map(groups.map(group => [group.name.replace(/^(Clone of )+/, ""), group]));
    if (!["idle", "walk", "run"].every(name => byName.has(name))) return null;
    return new GroundedNpcGait({ idle: byName.get("idle")!, walk: byName.get("walk")!, run: byName.get("run")! }, scale, clips);
  }

  private constructor(groups: GroundedNpcGait["groups"], private readonly scale: number, private readonly clips: NpcStrideClips) {
    this.groups = groups;
    for (const group of Object.values(groups)) group.start(true, 1);
    this.settle();
  }

  update(speed: number, dt: number): void {
    if (this.suspended || dt <= 0) return;
    if (speed <= .025) {
      this.settle();
      this.groups.walk.speedRatio = this.groups.run.speedRatio = 0;
      return;
    }
    const walkNatural = this.clips.walk.stride_length * this.scale / this.clips.walk.duration;
    const runNatural = this.clips.run.stride_length * this.scale / this.clips.run.duration;
    const runStart = walkNatural * 1.6;
    const runFull = Math.max(runStart + .5, runNatural * .8);
    const move = Math.min(1, Math.max(0, (speed - .025) / .15));
    const run = Math.min(1, Math.max(0, (speed - runStart) / (runFull - runStart)));
    const targets = { idle: 1 - move, walk: move * (1 - run), run: move * run };
    const blend = 1 - Math.exp(-dt / .08);
    for (const name of ["idle", "walk", "run"] as const) {
      const group = this.groups[name];
      group.weight += (targets[name] - group.weight) * blend;
    }
    // The source's stance sweep/stride determines paw travel, including slow starts.
    this.groups.walk.speedRatio = speed / walkNatural;
    this.groups.run.speedRatio = speed / runNatural;
  }

  settle(): void {
    this.groups.idle.weight = 1;
    this.groups.walk.weight = this.groups.run.weight = 0;
  }

  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
    this.settle();
    for (const group of Object.values(this.groups)) {
      if (suspended) group.pause();
      else group.play(true);
    }
  }

  dispose(): void {
    for (const group of Object.values(this.groups)) group.stop();
  }
}
